'use client';

import { useRef } from 'react';
import {
  CalendarDays,
  ChevronLeft,
  Church,
  Clock,
  Heart,
  Info,
  MapPin,
  MessageCircleQuestion,
  ShieldCheck,
  Users,
} from 'lucide-react';
import type { FinderOpportunity, MyServeResponse } from '@/lib/types';
import { campusLabel, fmtDay, parseMp } from '@/lib/format';
import { accentTokens, isHexColor } from '@/lib/accent';
import { useEventChrome } from '@/lib/useEventChrome';
import { WoodsideLogo } from '@church/nextjs-ui/website';
import { Avatar } from './Avatar';
import { CampusIcon } from './CampusIcon';
import { ChecklistCard } from './ChecklistCard';
import { RichDescription } from './RichDescription';
import { ResponseForm } from './ResponseForm';
import { ServeArt, serveRequirements, serveShift } from './ServeCard';
import { Tag } from './chips';

/**
 * ONE OPPORTUNITY, DRESSED LIKE AN EVENT.
 *
 * The details page wears the event-details widget's layout so serving and
 * attending read as one product: a band with the art blown up and blurred
 * behind the poster, the facts beside it under condensed labels, a card of
 * rows (campus, ministry, who to ask), the description, and the sign-up in a
 * sticky card on the right where an event keeps "Who".
 *
 * AN OPPORTUNITY CAN WEAR ITS OWN COLOUR. Opportunities.Accent_Color and
 * Accent_Ink, by the same rules as an event's (lib/accent.ts): the accent
 * changes the things you press and the marks, never the text. With none set
 * every var(--accent, #299c8f) falls back to the church's teal, so an
 * opportunity nobody has coloured looks exactly as it did.
 *
 * The finders link here directly; nothing opens an opportunity in a sheet.
 */

interface OpportunityPageProps {
  apiBaseUrl: string;
  opportunity: FinderOpportunity;
  responses: MyServeResponse[];
  onResponded: () => void;
  finderUrl: string;
  showFullAddress: boolean;
}

const KICKER =
  'font-[family-name:var(--font-display)] text-[15px] leading-none tracking-[0.06em] uppercase';

function Fact({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[22px_1fr] gap-x-3 gap-y-1.5">
      <span className="mt-px text-white/80">{icon}</span>
      <span className={`${KICKER} text-white/75`}>{label}</span>
      <div className="col-start-2 text-[15px] font-semibold text-white">{children}</div>
    </div>
  );
}

function Row({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[22px_1fr] gap-x-3 gap-y-1 px-5 py-4">
      <span className="mt-px text-[var(--accent-text,var(--accent,#299c8f))]">{icon}</span>
      <span className={`${KICKER} text-[13px] text-gray-500`}>{label}</span>
      <div className="col-start-2 flex min-w-0 items-center gap-2.5 font-bold">{children}</div>
    </div>
  );
}

export function OpportunityPage({
  apiBaseUrl,
  opportunity: o,
  responses,
  onResponded,
  finderUrl,
  showFullAddress,
}: OpportunityPageProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  useEventChrome(rootRef, o.accentColor, o.accentInk, o.id);
  const accent = isHexColor(o.accentColor) ? accentTokens(o.accentColor, o.accentInk) : null;
  const vars = accent
    ? ({
        '--accent': accent.base,
        '--on-accent': accent.onAccent,
        '--accent-text': accent.text,
        '--accent-deep': accent.deep,
        '--accent-soft': accent.soft,
      } as React.CSSProperties)
    : undefined;

  const shift = serveShift(o);
  const day = o.date ? fmtDay(parseMp(o.date)) : null;
  const duration =
    o.durationHours && !shift ? `${o.durationHours} hour${o.durationHours === 1 ? '' : 's'}` : null;
  const place = o.locationName ?? campusLabel(o.congregationId, o.congregationName);
  const street =
    showFullAddress && o.address
      ? [o.address.line1, [o.address.city, o.address.postalCode].filter(Boolean).join(' ')]
          .filter((l): l is string => Boolean(l && l.trim()))
          .join(', ')
      : null;
  const requirements = serveRequirements(o);
  const full = o.remaining === 0;
  const spots =
    o.remaining === null || o.maximumNeeded === null
      ? null
      : o.remaining === 0
        ? 'The team is full right now'
        : `${o.remaining} of ${o.maximumNeeded} spots left`;

  return (
    <div ref={rootRef} className="sf flex flex-col gap-7 text-[#1c2b39]" style={vars}>
      {/* The band: the art blown up behind, the poster in front, the facts beside it. */}
      <section className="bleed relative overflow-hidden bg-[color-mix(in_srgb,var(--accent,#1c2b39)_45%,#1c2b39)] text-white">
        <div className="pointer-events-none absolute inset-0 scale-125 blur-3xl" aria-hidden="true">
          <ServeArt opportunity={o} />
        </div>
        <div className="absolute inset-0 bg-black/45" aria-hidden="true" />
        <div className="bleed-inner bleed-headroom relative flex flex-col gap-6 pb-7 sm:pb-9">
          <a
            href={finderUrl}
            className={`${KICKER} inline-flex w-fit items-center gap-1 text-white/85 no-underline transition-opacity hover:text-white`}
          >
            <ChevronLeft className="h-3.5 w-3.5" strokeWidth={3} />
            All opportunities
          </a>
          <div className="grid items-start gap-6 md:grid-cols-[minmax(0,364px)_1fr] md:gap-8">
            <div className="aspect-video w-full overflow-hidden rounded-2xl shadow-[0_18px_40px_rgba(0,0,0,.35)] ring-1 ring-white/20">
              <ServeArt opportunity={o} />
            </div>
            <div className="flex min-w-0 flex-col gap-4">
              <h1 className="text-2xl leading-tight font-extrabold tracking-tight uppercase sm:text-3xl">
                {o.title}
              </h1>
              {o.description && (
                <Fact icon={<Info className="h-[18px] w-[18px]" />} label="What">
                  <RichDescription
                    text={o.description}
                    className="text-[15px] font-medium text-white/90 [&_a]:text-white"
                  />
                </Fact>
              )}
              <Fact icon={<Clock className="h-[18px] w-[18px]" />} label="When">
                <span className="block">{day ?? 'Ongoing, join any time'}</span>
                {(shift || duration) && (
                  <span className="block text-sm font-medium text-white/75">
                    {shift ?? duration}
                  </span>
                )}
              </Fact>
              <Fact icon={<MapPin className="h-[18px] w-[18px]" />} label="Where">
                <span className="block">{place}</span>
                {street && (
                  <span className="block text-sm font-medium text-white/75">{street}</span>
                )}
              </Fact>
            </div>
          </div>
        </div>
      </section>

      <div className="grid gap-7 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-4">
          <div className="divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white shadow-sm">
            <Row icon={<Church className="h-[18px] w-[18px]" />} label="Campus">
              <CampusIcon congregationId={o.congregationId} className="h-7 w-7" />
              <span className="truncate">{campusLabel(o.congregationId, o.congregationName)}</span>
            </Row>
            {o.ministryName && (
              <Row icon={<Users className="h-[18px] w-[18px]" />} label="Ministry">
                <span className="font-[family-name:var(--font-display)] text-[26px] leading-none font-normal tracking-[0.02em] uppercase">
                  {o.ministryName}
                </span>
              </Row>
            )}
            {o.contact && (
              <Row icon={<MessageCircleQuestion className="h-[18px] w-[18px]" />} label="Questions">
                <Avatar name={o.contact.name} imageUrl={o.contact.imageUrl} size={32} />
                <span className="truncate">{o.contact.name}</span>
              </Row>
            )}
          </div>

          {/* Only what the rows above and the Who card do not already say:
              campus, ministry and spots each have a home on this page. */}
          {(requirements.length > 0 || o.backgroundCheck || o.addToEventTitle) && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {requirements.map((r) => (
                <Tag key={r}>{r}</Tag>
              ))}
              {o.backgroundCheck && (
                <Tag icon={<ShieldCheck className="h-3.5 w-3.5 text-gray-500" />}>
                  Background check
                </Tag>
              )}
              {o.addToEventTitle && (
                <Tag icon={<CalendarDays className="h-3.5 w-3.5 text-gray-500" />}>
                  Part of {o.addToEventTitle}
                </Tag>
              )}
            </div>
          )}

          {o.checklist && (
            <div className="mt-3">
              <ChecklistCard checklist={o.checklist} scope={`opportunity-${o.id}`} />
            </div>
          )}
        </div>

        <aside className="min-w-0 scroll-mt-6" id="serve-who">
          <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm lg:sticky lg:top-24">
            <span className={`${KICKER} text-gray-500`}>Who</span>
            {spots && <p className="-mt-2 text-sm text-gray-500">{spots}</p>}
            {full &&
            !responses.some(
              (r) => r.status === 'pending' || r.status === 'placed'
            ) /* A full team takes no sign-up: the line above says so, up front
                 rather than as an error after Send. */ ? null : (
              <ResponseForm
                apiBaseUrl={apiBaseUrl}
                opportunity={o}
                finderUrl={finderUrl}
                responded={responses}
                onSent={onResponded}
                stepped
                accentStyle={vars}
                bandSubtitle={[
                  day ?? 'Ongoing',
                  campusLabel(o.congregationId, o.congregationName),
                ].join(' · ')}
              />
            )}
          </div>
        </aside>
      </div>

      {/*
        On a phone the Who card sits below the whole description, so a dock
        at the bottom of the screen takes you to it, the way an event's
        register dock does. Hidden once the columns sit side by side.
      */}
      {!o.externalRegistrationUrl && !full && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/85 px-4 pt-3 pb-[calc(12px+env(safe-area-inset-bottom,0px))] backdrop-blur lg:hidden">
          <button
            type="button"
            onClick={(e) =>
              (e.currentTarget.getRootNode() as Document | ShadowRoot)
                .getElementById?.('serve-who')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
            className="flex w-full items-center justify-center gap-1.5 rounded-full bg-[var(--accent,#299c8f)] px-6 py-3 text-base font-bold text-[var(--on-accent,#fff)] shadow-sm transition-all active:scale-[0.98]"
          >
            Sign up
          </button>
        </div>
      )}
      <div className="h-20 lg:hidden" aria-hidden="true" />
    </div>
  );
}

/** A bar standing in for a value, on the band or on paper. */
function Bar({ w, art = false, h = 'h-4' }: { w: string; art?: boolean; h?: string }) {
  return <div className={`${h} ${art ? 'skeleton-art' : 'skeleton'}`} style={{ width: w }} />;
}

/**
 * THE SHAPE OF THE PAGE, BEFORE THE PAGE.
 *
 * The event-details widget's skeleton, for an opportunity. It sits in the same
 * `sf` root with the same `bleed` band, so it is measured the same way and
 * stands in exactly the space the band is about to take, under the site's
 * header rather than pushed up into it.
 *
 * Only what has to be read from MP waits: the art, the title, the words, the
 * facts and who is going. "All opportunities", WHAT / WHEN / WHERE, Campus /
 * Ministry / Questions and Who are the page's own words and are drawn from the
 * first frame.
 *
 * The band is the church's navy with two slow washes of teal behind the
 * Woodside mark: the colour the real band falls back to with no accent, and one
 * the transparent header can always be read against.
 */
export function OpportunitySkeleton({ finderUrl }: { finderUrl: string }) {
  return <PageSkeleton kind="opportunity" backHref={finderUrl} backLabel="All opportunities" />;
}

/**
 * The same loading page for a Collection and for /care, each with its own
 * fixed words: the back link, What/When/Where, and the labels of the cards
 * below the band. Only what MP has to answer shimmers.
 */
export function PageSkeleton({
  kind,
  backHref,
  backLabel,
}: {
  kind: 'opportunity' | 'collection' | 'care';
  backHref?: string;
  backLabel?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  useEventChrome(rootRef, null, null, 'skeleton');

  return (
    <div ref={rootRef} className="sf flex flex-col gap-7 text-[#1c2b39]" aria-busy="true">
      <section className="bleed relative overflow-hidden bg-[#1c2b39] text-white">
        <div className="aurora" aria-hidden="true" />
        <div className="bleed-inner bleed-headroom relative flex flex-col gap-6 pb-7 sm:pb-9">
          {backHref && (
            <a
              href={backHref}
              className={`${KICKER} inline-flex w-fit items-center gap-1 text-white/85 no-underline transition-opacity hover:text-white`}
            >
              <ChevronLeft className="h-3.5 w-3.5" strokeWidth={3} />
              {backLabel}
            </a>
          )}
          <div className="grid items-start gap-6 md:grid-cols-[minmax(0,364px)_1fr] md:gap-8">
            {/* Not an empty box: the church's mark, quietly, in the frame the art is about to fill. */}
            <div className="flex aspect-video w-full items-center justify-center rounded-2xl bg-white/5 ring-1 ring-white/10">
              <WoodsideLogo tone="current" className="h-14 w-14 text-white/20" />
            </div>
            <div className="flex min-w-0 flex-col gap-4">
              <Bar art w="70%" h="h-8" />
              <Fact
                icon={
                  kind === 'opportunity' ? (
                    <Info className="h-[18px] w-[18px]" />
                  ) : (
                    <Heart className="h-[18px] w-[18px]" />
                  )
                }
                label="What"
              >
                <div className="space-y-2 pt-1">
                  <Bar art w="100%" />
                  <Bar art w="92%" />
                  <Bar art w="64%" />
                </div>
              </Fact>
              <Fact icon={<Clock className="h-[18px] w-[18px]" />} label="When">
                <div className="space-y-2 pt-1">
                  <Bar art w="9rem" />
                  <Bar art w="5rem" />
                </div>
              </Fact>
              <Fact icon={<MapPin className="h-[18px] w-[18px]" />} label="Where">
                <div className="pt-1">
                  <Bar art w="11rem" />
                </div>
              </Fact>
            </div>
          </div>
        </div>
      </section>

      <div className="grid gap-7 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-4">
          {kind === 'care' ? (
            <>
              <span className={`${KICKER} text-gray-500`}>Your zip code</span>
              <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
                <Bar w="7rem" h="h-9" />
              </div>
              <span className={`${KICKER} mt-3 text-gray-500`}>Every location</span>
              <SkeletonRows />
            </>
          ) : (
            <>
              <div className="divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white shadow-sm">
                <Row icon={<Church className="h-[18px] w-[18px]" />} label="Campus">
                  {/* A Collection is church-wide; the campus is chosen in its sheet. */}
                  {kind === 'collection' ? (
                    <>
                      <WoodsideLogo
                        tone="current"
                        className="h-7 w-7 shrink-0 text-[var(--accent-text,var(--accent,#299c8f))]"
                      />
                      <span>All campuses</span>
                    </>
                  ) : (
                    <>
                      <div className="skeleton h-7 w-7 shrink-0 rounded-full" />
                      <Bar w="9rem" />
                    </>
                  )}
                </Row>
                <Row icon={<Users className="h-[18px] w-[18px]" />} label="Ministry">
                  <Bar w="11rem" h="h-6" />
                </Row>
                {kind === 'opportunity' && (
                  <Row
                    icon={<MessageCircleQuestion className="h-[18px] w-[18px]" />}
                    label="Questions"
                  >
                    <div className="skeleton h-8 w-8 shrink-0 rounded-full" />
                    <Bar w="8rem" />
                  </Row>
                )}
              </div>
              {kind === 'collection' && (
                <>
                  <span className={`${KICKER} mt-3 text-gray-500`}>Checklist</span>
                  <SkeletonRows />
                </>
              )}
            </>
          )}
        </div>
        <aside className="min-w-0">
          <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <span className={`${KICKER} text-gray-500`}>
              {kind === 'care' ? 'Your request' : 'Who'}
            </span>
            {kind === 'opportunity' ? (
              <>
                <Bar w="7rem" h="h-3.5" />
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-3 py-1">
                    <div className="skeleton h-5 w-5 shrink-0 rounded-md" />
                    <div className="skeleton h-9 w-9 shrink-0 rounded-full" />
                    <Bar w={i === 0 ? '8rem' : '6.5rem'} />
                  </div>
                ))}
              </>
            ) : (
              <Bar w="9rem" h="h-3.5" />
            )}
            <div className="skeleton h-12 w-full rounded-full" />
          </div>
        </aside>
      </div>
    </div>
  );
}

/** Three list rows: a date column, a name and a line. */
function SkeletonRows() {
  return (
    <div className="divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white shadow-sm">
      {[0, 1, 2].map((i) => (
        <div key={i} className="grid grid-cols-[64px_1fr] items-start gap-x-3.5 gap-y-2 px-5 py-4">
          <div className="justify-self-end">
            <Bar w="2.5rem" />
          </div>
          <div className="space-y-2">
            <Bar w={i === 1 ? '55%' : '70%'} />
            <Bar w="40%" h="h-3" />
          </div>
        </div>
      ))}
    </div>
  );
}
